/**
 * Item → Planning Engine Adapter
 *
 * Converts live `Item` objects from the prioritization store into
 * `PlanningEngineInput` so the planning engine can run against real
 * system data without requiring separate manual entry into planning tables.
 *
 * Field mapping rationale (viability planning):
 *   Item Effort (CU) / altitude workSize / altitude level → capacity units
 *   exit_status                    → includedInPlan / WorkUnitState
 *   planning_maturity              → workDefinitionStatus
 *   Item relationships (edges)     → PriorityDependency[] (hard/gate subtypes)
 *
 * Vocabulary: capacity unit (CU) = demand measure; work package = decomposed object.
 */

import type { Item } from '../types/prioritization/lifecycle';
import type {
  Priority,
  WorkUnit,
  Dependency,
  PriorityDependency,
  PlanningEngineInput,
  PlanningRelationshipGroup,
  Scenario,
  WorkDefinitionStatus,
  ItemPlanningBasis,
  CapacityModelRow,
  PriorityCapacityRow,
  TeamParticipation,
} from './planningEngineModule.types';
import type { ItemRelationshipEdge } from '../types/prioritization/itemRelationships';

// ---------------------------------------------------------------------------
// Work-size → hours midpoint → capacity units (CU ≈ 8h)
// System defaults; workspaces may override via planning_workspace_defaults.item_effort_band_hours.
// ---------------------------------------------------------------------------

export const WORK_SIZE_BAND_KEYS = [
  '1-2_weeks',
  '3-8_weeks',
  '9-26_weeks',
  '27-52_weeks',
  '52+_weeks',
] as const;

export type WorkSizeBandKey = (typeof WORK_SIZE_BAND_KEYS)[number];

/** System-default hours midpoints (also the Reset target in Planning Defaults). */
export const WORK_SIZE_HOURS: Record<WorkSizeBandKey, number> = {
  '1-2_weeks': 60,      // ~1.5 weeks @ 40h
  '3-8_weeks': 200,     // ~5 weeks @ 40h
  '9-26_weeks': 600,    // ~15 weeks @ 40h
  '27-52_weeks': 1500,  // ~37 weeks @ 40h
  '52+_weeks': 2500,    // rough upper bound
};

/** Partial override map from workspace settings (any subset of band keys). */
export type WorkSizeHoursOverrides = Partial<Record<WorkSizeBandKey, number>>;

/** Inverse of WORK_SIZE_SCORES — altitude 1–5 → work-size band when workSize was not answered. */
export const ALTITUDE_LEVEL_TO_WORK_SIZE: Record<number, WorkSizeBandKey> = {
  1: '1-2_weeks',
  2: '3-8_weeks',
  3: '9-26_weeks',
  4: '27-52_weeks',
  5: '52+_weeks',
};

/** Display rows for Planning Defaults: altitude level + band key. */
export const ALTITUDE_EFFORT_BANDS: ReadonlyArray<{
  altitude: 1 | 2 | 3 | 4 | 5;
  band: WorkSizeBandKey;
}> = [
  { altitude: 1, band: '1-2_weeks' },
  { altitude: 2, band: '3-8_weeks' },
  { altitude: 3, band: '9-26_weeks' },
  { altitude: 4, band: '27-52_weeks' },
  { altitude: 5, band: '52+_weeks' },
];

export const HOURS_PER_CAPACITY_UNIT = 8;

/**
 * Merge workspace overrides onto system defaults.
 * Invalid / non-positive values fall back to the system default for that band.
 */
export function mergeWorkSizeHours(
  overrides?: WorkSizeHoursOverrides | null,
): Record<WorkSizeBandKey, number> {
  const out = { ...WORK_SIZE_HOURS };
  if (!overrides || typeof overrides !== 'object') return out;
  for (const key of WORK_SIZE_BAND_KEYS) {
    const raw = overrides[key];
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
      out[key] = raw;
    }
  }
  return out;
}

/** Parse JSONB from planning_workspace_defaults.item_effort_band_hours. */
export function parseItemEffortBandHours(raw: unknown): WorkSizeHoursOverrides {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: WorkSizeHoursOverrides = {};
  const obj = raw as Record<string, unknown>;
  for (const key of WORK_SIZE_BAND_KEYS) {
    const n = Number(obj[key]);
    if (Number.isFinite(n) && n > 0) out[key] = n;
  }
  return out;
}

export type EffortSource =
  | 'explicit'
  | 'planner_override'
  | 'work_size'
  | 'altitude_level_inferred';

export interface ResolvedItemEffort {
  capacityUnits: number | null;
  hours: number;
  source: EffortSource | null;
  workSizeBand: string | null;
  /** True when CU came from altitude workSize or altitude-level inference (not Item Effort). */
  isDefaultFromAltitude: boolean;
  /** True when sized from altitude 1–5 without an intentional workSize answer. */
  isNonIntentional: boolean;
}

function workSizeToHours(
  workSize: string | undefined | null,
  hoursMap: Record<WorkSizeBandKey, number>,
): number {
  if (!workSize) return 0;
  if ((WORK_SIZE_BAND_KEYS as readonly string[]).includes(workSize)) {
    return hoursMap[workSize as WorkSizeBandKey] ?? 0;
  }
  return 0;
}

function hoursToCapacityUnits(hours: number): number | null {
  if (hours <= 0) return null;
  return Math.ceil(hours / HOURS_PER_CAPACITY_UNIT);
}

/**
 * Resolve Item Effort / altitude defaults into capacity units for viability planning.
 * Order: Item Effort → intentional workSize → altitude level inferred → null.
 * `bandHours` = workspace overrides (merged with system defaults).
 */
export function resolveItemEffortCapacityUnits(
  item: Item,
  bandHours?: WorkSizeHoursOverrides | null,
): ResolvedItemEffort {
  const hoursMap = mergeWorkSizeHours(bandHours);
  const stored = item.effortCapacityUnits;
  if (stored != null && Number.isFinite(stored) && stored > 0) {
    const source = (item.effortSource ?? 'explicit') as EffortSource;
    return {
      capacityUnits: stored,
      hours: stored * HOURS_PER_CAPACITY_UNIT,
      source,
      workSizeBand: item.altitudeScopingInputs?.workSize ?? null,
      isDefaultFromAltitude: source === 'work_size' || source === 'altitude_level_inferred',
      isNonIntentional: source === 'altitude_level_inferred',
    };
  }

  const workSize = item.altitudeScopingInputs?.workSize;
  if (workSize) {
    const hours = workSizeToHours(workSize, hoursMap);
    return {
      capacityUnits: hoursToCapacityUnits(hours),
      hours,
      source: 'work_size',
      workSizeBand: workSize,
      isDefaultFromAltitude: true,
      isNonIntentional: false,
    };
  }

  const level = Number(item.altitudeLevel);
  if (level >= 1 && level <= 5) {
    const band = ALTITUDE_LEVEL_TO_WORK_SIZE[level];
    const hours = workSizeToHours(band, hoursMap);
    return {
      capacityUnits: hoursToCapacityUnits(hours),
      hours,
      source: 'altitude_level_inferred',
      workSizeBand: band,
      isDefaultFromAltitude: true,
      isNonIntentional: true,
    };
  }

  return {
    capacityUnits: null,
    hours: 0,
    source: null,
    workSizeBand: null,
    isDefaultFromAltitude: false,
    isNonIntentional: false,
  };
}

/**
 * The plan keeps its OWN copy of each priority's size, in
 * `planning_priorities.priority_level_wu_estimate`. That is deliberate: a plan
 * that silently re-costs itself every time someone edits an item is not a plan.
 *
 * What was missing is that the copy never announced itself. On the Sightline
 * plan 8 of 18 priorities charged a number their item no longer agreed with —
 * SOC 2 costed at 188 CU for a 25 CU job — and nothing on any screen said so.
 * Worse, the Priorities grid printed the item's CURRENT band beside the plan's
 * STALE number in one cell, so the two halves of a single cell contradicted
 * each other.
 *
 * So: compare, show, and let a person pull the item's value in on purpose.
 * Never adopt it for them.
 *
 * Returns null when there is nothing to report — no plan estimate, no item
 * estimate, or the two agree.
 */
export interface PlanEffortDivergence {
  /** What the plan charges, in capacity units. */
  planCapacityUnits: number;
  /** What the item says today, in capacity units. */
  itemCapacityUnits: number;
  /** The item's current size band, for labelling the item's number (not the plan's). */
  itemWorkSizeBand: string | null;
  /** True when the plan charges more than the item now claims. */
  planCostsMore: boolean;
}

export function planEffortDivergence(
  planCapacityUnits: number | null | undefined,
  item: Item | null | undefined,
  bandHours?: WorkSizeHoursOverrides | null,
): PlanEffortDivergence | null {
  if (planCapacityUnits == null || !Number.isFinite(planCapacityUnits)) return null;
  if (!item) return null;
  const resolved = resolveItemEffortCapacityUnits(item, bandHours);
  const itemCu = resolved.capacityUnits;
  if (itemCu == null || !Number.isFinite(itemCu)) return null;
  if (itemCu === planCapacityUnits) return null;
  return {
    planCapacityUnits,
    itemCapacityUnits: itemCu,
    itemWorkSizeBand: resolved.workSizeBand,
    planCostsMore: planCapacityUnits > itemCu,
  };
}

/** Seed CU + source for altitude-only picks (does not write altitude workSize). */
export function effortFieldsFromAltitudeLevel(
  level: number,
  bandHours?: WorkSizeHoursOverrides | null,
): {
  effortCapacityUnits: number;
  effortSource: 'altitude_level_inferred';
} {
  const hoursMap = mergeWorkSizeHours(bandHours);
  const band = ALTITUDE_LEVEL_TO_WORK_SIZE[level] ?? '3-8_weeks';
  const hours = workSizeToHours(band, hoursMap);
  return {
    effortCapacityUnits: hoursToCapacityUnits(hours) ?? 25,
    effortSource: 'altitude_level_inferred',
  };
}

/** Seed CU from intentional workSize unless Effort is already planner/explicit. */
export function effortFieldsFromWorkSize(
  workSize: string,
  currentSource: Item['effortSource'],
  bandHours?: WorkSizeHoursOverrides | null,
): { effortCapacityUnits: number; effortSource: 'work_size' } | null {
  if (currentSource === 'planner_override' || currentSource === 'explicit') return null;
  const hoursMap = mergeWorkSizeHours(bandHours);
  const hours = workSizeToHours(workSize, hoursMap);
  const cu = hoursToCapacityUnits(hours);
  if (cu == null) return null;
  return { effortCapacityUnits: cu, effortSource: 'work_size' };
}

// ---------------------------------------------------------------------------
// Stewardship bucket → capacity resource type
// ---------------------------------------------------------------------------

/** Resource pool used when an item carries no stewardship bucket (or is unassigned). */
export const DEFAULT_ITEM_RESOURCE_TYPE = 'engineering';

/**
 * Map an item's stewardship bucket (the functional area it was routed to) onto
 * the planning engine's resource-type vocabulary, so synced demand lands in the
 * right capacity pool instead of piling onto `engineering` for every item.
 * Buckets with no natural pool fall back to the default.
 */
export const STEWARDSHIP_BUCKET_TO_RESOURCE_TYPE: Record<string, string> = {
  engineering: 'engineering',
  product: 'pm',
  operations: 'devops',
  marketing: 'marketing',
  sales: 'sales',
  support: 'support',
  finance: 'finance',
  legal: 'legal',
  hr: 'other',
  general: DEFAULT_ITEM_RESOURCE_TYPE,
};

/** Resource type an item consumes from, derived from its stewardship bucket. */
export function itemResourceType(item: Item): string {
  const bucket = item.stewardship?.bucketId;
  if (!bucket) return DEFAULT_ITEM_RESOURCE_TYPE;
  return STEWARDSHIP_BUCKET_TO_RESOURCE_TYPE[bucket] ?? String(bucket);
}

// ---------------------------------------------------------------------------
// planning_maturity → WorkDefinitionStatus
// ---------------------------------------------------------------------------

function toWorkDefinitionStatus(maturity: string | undefined | null): WorkDefinitionStatus {
  if (!maturity || maturity === 'directional') return 'not_decomposed';
  // The enum value is 'detailed_plan_exists' (migration 412); this once tested for 'detailed',
  // which can never occur, so no item ever reached fully_decomposed.
  if (maturity === 'detailed_plan_exists') return 'fully_decomposed';
  return 'partially_decomposed';
}

/** Notes prefix for priority deps materialized from item relationship edges (§4.7). */
export const PLANNING_SYNC_DEP_NOTE_PREFIX = 'sync:item_relationship:';

export function itemToPriority(
  item: Item,
  scenarioId: string | null,
  bandHours?: WorkSizeHoursOverrides | null,
  /**
   * Where the organization ranked this item, 1-based, from the priority list it
   * accepted. This used to be hardcoded null, so the engine sorted by
   * `forceRank ?? Infinity` then createdAt — pool order — and the agreed
   * ranking had no effect on the plan at all. On the Sightline cycle that put
   * the room's #1, #2 and #3 below the line.
   *
   * It belongs on the BASE priority, not the override: mergePriorityWithOverride
   * takes `override.forceRank ?? base.forceRank`, so a rank set deliberately in
   * planning still wins over the room's, which is the right precedence and is
   * recorded as an override.
   */
  acceptedRank?: number | null,
): Priority {
  const resolved = resolveItemEffortCapacityUnits(item, bandHours);
  const effortHours = resolved.hours;
  const workDefinitionStatus = toWorkDefinitionStatus(item.planning_maturity);
  const itemPlanningBasis: ItemPlanningBasis = 'priority_estimate';

  return {
    id: item.id,
    projectId: item.workspace_id ?? '',
    scenarioId,
    name: item.title,
    category: item.itemCategory ?? null,
    altitudeBand: item.altitudeLevel != null ? String(item.altitudeLevel) : null,
    track: null,
    itemType: item.itemCategory ?? null,
    source: item.source ?? null,
    confidence: null,
    strategicObjective: null,
    accountableLeader: item.ownerId ?? null,
    forceRank: acceptedRank ?? null,
    aboveTheLine: item.exit_status === 'active' ? true : null,
    valueScore: null,
    riskReduction: null,
    complianceFlag: null,
    executiveImperative: item.isStrategicallyNominated ?? null,
    costOfDelay: null,
    impactScore: null,
    effortScore: effortHours > 0 ? Math.round((effortHours / 600) * 10) : null,
    cognitiveComplexity: null,
    workDefinitionStatus,
    itemPlanningBasis,
    priorityLevelWuEstimate: resolved.capacityUnits,
    remainingWuEstimate: null,
    priorityResourceType: itemResourceType(item),
    notes: item.description ?? null,
    // Launch/ownership fields are set in Planning, not carried from the item —
    // but they are declared on Priority, and omitting them left every synced
    // priority structurally incomplete (5 of this file's type errors). Explicit
    // nulls: a live item genuinely has no launch window or owning org yet.
    targetLaunchStart: null,
    targetLaunchEnd: null,
    launchProfileId: null,
    owningOrg: null,
    owningOrgUnitId: null,
    createdAt: item.createdAt instanceof Date ? item.createdAt.toISOString() : String(item.createdAt),
    updatedAt: item.updatedAt instanceof Date ? item.updatedAt.toISOString() : String(item.updatedAt),
  };
}

/** Notes prefix for work units materialized from prioritization items (§4.7). */
export const PLANNING_SYNC_WU_NOTE_PREFIX = 'sync:prioritization_item:';

// ---------------------------------------------------------------------------
// Item → WorkUnit (1:1 placeholder when item not decomposed)
// ---------------------------------------------------------------------------

function itemExitToWorkUnitState(exitStatus: Item['exit_status']): WorkUnit['state'] {
  if (exitStatus === 'archived') return 'cancelled';
  if (exitStatus === 'deferred') return 'backlog';
  return 'backlog';
}

export function itemToWorkUnit(
  item: Item,
  projectId: string,
  bandHours?: WorkSizeHoursOverrides | null,
): WorkUnit {
  const resolved = resolveItemEffortCapacityUnits(item, bandHours);
  const effortHours = resolved.hours;
  const now = new Date().toISOString();

  return {
    id: item.id,
    projectId,
    priorityId: item.id,
    parentWorkUnitId: null,
    title: item.title,
    description: item.description ?? null,
    // A placeholder unit standing in for an item nobody has decomposed yet, so
    // there is no execution recipe behind it and no per-work-type uncertainty to
    // apply. That matches what this path is for: the rough cut, priced off the
    // item's own estimate, where uncertainty is carried by the scenario buffer.
    resolvedRecipeId: null,
    workProfile: itemResourceType(item),
    requiredCapability: null,
    assignedOwner: item.ownerId ?? null,
    effortEstimateHours: effortHours > 0 ? effortHours : 8,
    durationEstimateDays: null,
    countedEffortHours: null,
    state: itemExitToWorkUnitState(item.exit_status),
    assumptionFlag: null,
    riskLevel: null,
    milestone: null,
    includedInPlan: !item.exit_status || item.exit_status === 'active',
    constraintAdjustedRank: null,
    rankDelta: null,
    externalIssueKey: null,
    sourceId: item.id,
    sourceType: 'prioritization_item',
    sourceUrl: null,
    customerName: null,
    requestedBy: null,
    requestedByOrg: null,
    systemsTouched: null,
    workflowsTouched: null,
    personasImpacted: null,
    notes: `${PLANNING_SYNC_WU_NOTE_PREFIX}${item.id}`,
    createdAt: item.createdAt instanceof Date ? item.createdAt.toISOString() : String(item.createdAt ?? now),
    updatedAt: item.updatedAt instanceof Date ? item.updatedAt.toISOString() : String(item.updatedAt ?? now),
  };
}

export function edgeToWorkUnitDependency(
  edge: ItemRelationshipEdge,
  projectId: string,
): Dependency | null {
  const priorityDep = edgeToPriorityDependency(edge, projectId);
  if (!priorityDep) return null;
  return {
    id: edge.id,
    projectId,
    fromWorkUnitId: priorityDep.fromPriorityId,
    toWorkUnitId: priorityDep.toPriorityId,
    dependencyType: priorityDep.dependencyType,
    blocking: priorityDep.blocking,
    notes: `${PLANNING_SYNC_DEP_NOTE_PREFIX}${edge.id}`,
    createdAt: priorityDep.createdAt,
    updatedAt: priorityDep.updatedAt,
  };
}

// ---------------------------------------------------------------------------
// Item relationship edges → PriorityDependency[]
// ---------------------------------------------------------------------------

/**
 * Planning strength for a sequence edge's subtype.
 *
 * Previously only `hard` and `gate` produced a planning dependency and every
 * other sequence edge was discarded, so a relationship you created in
 * Prioritization simply never appeared in Planning — silently, on both
 * surfaces. In the Backspace workspace that was 8 of 90 sequence edges
 * carried; the other 82 were dropped. And it is the common case, not an edge
 * case: `AddSequenceLinkModal` defaults its subtype picker to `soft`, so the
 * default create flow produced an invisible-to-Planning link.
 *
 * Everything now carries. Strength decides whether it *constrains*:
 * `planningEngineModule` gates viability strictly on
 * `blocking && dependencyType === 'Hard'`, so a Soft/non-blocking row is
 * visible in the Dependencies list and inert in the engine's decisions.
 *
 * Legacy rows with no subtype are treated as Soft rather than Hard —
 * materializing 77 unreviewed edges as hard blockers would silently re-decide
 * plans, which is the opposite of the point.
 */
function planningStrengthForSubtype(
  subtype: string | null | undefined,
): { dependencyType: 'Hard' | 'Soft'; blocking: boolean } {
  if (subtype === 'hard') return { dependencyType: 'Hard', blocking: true };
  if (subtype === 'gate') return { dependencyType: 'Hard', blocking: false };
  return { dependencyType: 'Soft', blocking: false };
}

export function edgeToPriorityDependency(
  edge: ItemRelationshipEdge,
  projectId: string,
): PriorityDependency | null {
  if (edge.type !== 'dependency' && edge.type !== 'prerequisite') return null;

  const { dependencyType, blocking } = planningStrengthForSubtype(edge.subtype);
  const createdAt =
    edge.createdAt instanceof Date ? edge.createdAt.toISOString() : String(edge.createdAt ?? '');

  // Dependency reads source→target; prerequisite is the mirror — target requires
  // source first, which is the same constraint pointing the other way. Both
  // collapse to "fromPriority depends on toPriority".
  const [fromPriorityId, toPriorityId] =
    edge.type === 'dependency'
      ? [edge.sourceItemId, edge.targetItemId]
      : [edge.targetItemId, edge.sourceItemId];

  return {
    id: edge.id,
    projectId,
    fromPriorityId,
    toPriorityId,
    dependencyType,
    blocking,
    notes: null,
    createdAt,
    // ItemRelationshipEdge carries no updatedAt; the previous code read one
    // anyway, so every synced row landed with an empty string. Mirror createdAt
    // instead of fabricating a field that does not exist.
    updatedAt: createdAt,
  };
}

/**
 * True when dissolving this edge in Prioritization will also remove a
 * dependency from Planning on the next sync.
 *
 * The two surfaces are one graph with two views: Prioritization owns the edge,
 * Planning materializes it (`PLANNING_SYNC_DEP_NOTE_PREFIX`). Deleting on
 * either side used to be silent about the other, so a relationship could
 * disappear from a plan with no warning. Both surfaces now ask this same
 * question rather than each re-deriving which edge shapes matter — keep them
 * reading this predicate, not a copied `type`/`subtype` check.
 */
export function edgeMaterializesIntoPlanning(edge: ItemRelationshipEdge): boolean {
  // projectId is irrelevant to the shape test; the adapter only uses it to stamp the row.
  return edgeToPriorityDependency(edge, '') !== null;
}

// ---------------------------------------------------------------------------
// Default scenario for live-items mode (no Scenario record in DB)
// ---------------------------------------------------------------------------

export const LIVE_ITEMS_SCENARIO: Scenario = {
  id: 'live-items',
  projectId: '',
  planCycleId: null,
  name: 'Live Items (no scenario)',
  active: true,
  planningMode: 'priority_estimate',
  // The rough cut has no real work attached, so there are no work types to carry
  // uncertainty; it rides the scenario buffer.
  uncertaintyBasis: 'buffer',
  planningWeeks: 12,
  capacityBufferPercent: 0.2,
  defaultWorkUnitHours: 8,
  includeNotReady: false,
  includeBlocked: false,
  notes: 'Auto-generated from live prioritization items. No capacity model — demand only.',
  horizonStartDate: null,
  horizonEndDate: null,
  bucketGranularity: 'week',
  timePhasedEnabled: false,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// ---------------------------------------------------------------------------
// Main adapter: items + edges → PlanningEngineInput
// ---------------------------------------------------------------------------

export function itemsToPlanningEngineInput(
  items: Item[],
  edges: ItemRelationshipEdge[],
  scenario: Scenario = LIVE_ITEMS_SCENARIO,
  bandHours?: WorkSizeHoursOverrides | null,
): PlanningEngineInput {
  const activeItems = items.filter(
    (i) => !i.exit_status || i.exit_status === 'active',
  );
  const projectId = activeItems[0]?.workspace_id ?? '';

  const priorities: Priority[] = activeItems.map((i) =>
    itemToPriority(i, scenario.id, bandHours),
  );

  const priorityDependencies: PriorityDependency[] = edges
    .filter((e) => e.status !== 'dissolved')
    .flatMap((e) => {
      const dep = edgeToPriorityDependency(e, projectId);
      return dep ? [dep] : [];
    });

  const workUnits: WorkUnit[] = activeItems.map((i) => itemToWorkUnit(i, projectId, bandHours));

  const dependencies: Dependency[] = edges
    .filter((e) => e.status !== 'dissolved')
    .flatMap((e) => {
      const dep = edgeToWorkUnitDependency(e, projectId);
      return dep ? [dep] : [];
    });

  return {
    scenario,
    priorities,
    workUnits,
    dependencies,
    priorityDependencies,
    capacityRows: [],       // no capacity model — demand only
    priorityCapacityRows: [],
    teamParticipation: [],
  };
}

// ---------------------------------------------------------------------------
// Sync payload for planning_priorities + planning_priority_dependencies (§4.7)
// ---------------------------------------------------------------------------

export function buildPlanningSyncFromLiveItems(
  items: Item[],
  edges: ItemRelationshipEdge[],
  projectId: string,
  scenarioId: string | null,
  bandHours?: WorkSizeHoursOverrides | null,
): { priorities: Priority[]; priorityDependencies: PriorityDependency[] } {
  const activeItems = items.filter((i) => !i.exit_status || i.exit_status === 'active');
  const activeIdSet = new Set(activeItems.map((i) => i.id));

  const priorities = activeItems.map((item) => {
    const priority = itemToPriority(item, scenarioId, bandHours);
    return { ...priority, id: item.id, projectId };
  });

  const priorityDependencies: PriorityDependency[] = edges
    .filter((e) => e.status !== 'dissolved')
    .flatMap((e) => {
      const dep = edgeToPriorityDependency(e, projectId);
      if (!dep) return [];
      if (!activeIdSet.has(dep.fromPriorityId) || !activeIdSet.has(dep.toPriorityId)) return [];
      return [{
        ...dep,
        notes: `${PLANNING_SYNC_DEP_NOTE_PREFIX}${e.id}`,
      }];
    });

  return { priorities, priorityDependencies };
}

export function buildWorkUnitSyncFromLiveItems(
  items: Item[],
  edges: ItemRelationshipEdge[],
  projectId: string,
  bandHours?: WorkSizeHoursOverrides | null,
): { workUnits: WorkUnit[]; dependencies: Dependency[] } {
  const activeItems = items.filter((i) => !i.exit_status || i.exit_status === 'active');
  const activeIdSet = new Set(activeItems.map((i) => i.id));

  const workUnits = activeItems.map((item) => itemToWorkUnit(item, projectId, bandHours));

  const dependencies: Dependency[] = edges
    .filter((e) => e.status !== 'dissolved')
    .flatMap((e) => {
      const dep = edgeToWorkUnitDependency(e, projectId);
      if (!dep) return [];
      if (!activeIdSet.has(dep.fromWorkUnitId) || !activeIdSet.has(dep.toWorkUnitId)) return [];
      return [dep];
    });

  return { workUnits, dependencies };
}

// ---------------------------------------------------------------------------
// Manual override markers + scoped run input (DB edits must drive the engine)
// ---------------------------------------------------------------------------

/** Prefix written when a user saves edits in the Planning Engine work-units tab. */
export const PLANNING_MANUAL_WU_NOTE_PREFIX = 'manual:planning_engine:';

export function isSyncedWorkUnitNotes(notes: string | null | undefined): boolean {
  if (!notes) return false;
  if (notes.startsWith(PLANNING_MANUAL_WU_NOTE_PREFIX)) return false;
  return notes.startsWith(PLANNING_SYNC_WU_NOTE_PREFIX);
}

export function markWorkUnitManualOverride(notes: string | null | undefined): string {
  if (notes?.startsWith(PLANNING_MANUAL_WU_NOTE_PREFIX)) return notes;
  return `${PLANNING_MANUAL_WU_NOTE_PREFIX}${notes ?? ''}`;
}

export interface PlanningPriorityOverrideFields {
  forceRank: number | null;
  aboveTheLine: boolean | null;
  valueScore: number | null;
  effortScore: number | null;
  priorityLevelWuEstimate: number | null;
  priorityResourceType: string | null;
  notes: string | null;
}

export function mergePriorityWithOverride(
  base: Priority,
  override: PlanningPriorityOverrideFields | undefined,
): Priority {
  if (!override) return base;
  return {
    ...base,
    forceRank: override.forceRank ?? base.forceRank,
    aboveTheLine: override.aboveTheLine ?? base.aboveTheLine,
    valueScore: override.valueScore ?? base.valueScore,
    effortScore: override.effortScore ?? base.effortScore,
    priorityLevelWuEstimate: override.priorityLevelWuEstimate ?? base.priorityLevelWuEstimate,
    priorityResourceType: override.priorityResourceType ?? base.priorityResourceType,
    notes: override.notes ?? base.notes,
  };
}

/** Resolve work units for a scoped run — DB rows win over item-derived placeholders. */
export function resolveScopedWorkUnitsForRun(
  filteredItems: Item[],
  dbWorkUnits: WorkUnit[],
  projectId: string,
  scopedItemIdSet: Set<string> | null,
  bandHours?: WorkSizeHoursOverrides | null,
): WorkUnit[] {
  const dbById = new Map(dbWorkUnits.map((w) => [w.id, w]));
  const result: WorkUnit[] = [];
  const seen = new Set<string>();

  for (const item of filteredItems) {
    const wu = dbById.get(item.id) ?? itemToWorkUnit(item, projectId, bandHours);
    result.push(wu);
    seen.add(wu.id);
  }

  for (const wu of dbWorkUnits) {
    if (seen.has(wu.id)) continue;
    const inScope =
      scopedItemIdSet == null ||
      (wu.priorityId != null && scopedItemIdSet.has(wu.priorityId)) ||
      (wu.sourceId != null && scopedItemIdSet.has(wu.sourceId));
    if (inScope) {
      result.push(wu);
      seen.add(wu.id);
    }
  }

  return result;
}

export function filterDependenciesToWorkUnits(
  deps: Dependency[],
  workUnitIds: Set<string>,
): Dependency[] {
  return deps.filter(
    (d) => workUnitIds.has(d.fromWorkUnitId) && workUnitIds.has(d.toWorkUnitId),
  );
}

export function filterPriorityDepsToScope(
  deps: PriorityDependency[],
  priorityIds: Set<string>,
): PriorityDependency[] {
  return deps.filter(
    (d) => priorityIds.has(d.fromPriorityId) && priorityIds.has(d.toPriorityId),
  );
}

/**
 * Build engine input for a scoped run — merges live items with persisted
 * planning_priorities overrides and planning_work_units edits.
 *
 * Also appends unbound scenario `planning_priorities` (no matching live item),
 * matching the Priorities tab + Launch Calendar portfolio (Keith/Sample seeds).
 */
export function buildScopedPlanningEngineInput(args: {
  items: Item[];
  edges: ItemRelationshipEdge[];
  scenario: Scenario;
  projectId: string;
  scopedItemIds: Set<string> | null;
  /**
   * Which kind of Input Scope produced scopedItemIds. scenario_portfolio scopes
   * are priority-row membership (planning_priorities ids), not item ids — the
   * scenario's own rows ARE the run, whether or not they map to live items.
   */
  scopeKind?: string;
  dbWorkUnits: WorkUnit[];
  dbDependencies: Dependency[];
  dbPriorityDependencies: PriorityDependency[];
  /** All project planning_priorities; unbound scenario rows are merged into the run. */
  dbPriorities?: Priority[];
  priorityOverrides: Map<string, PlanningPriorityOverrideFields>;
  capacityRows: CapacityModelRow[];
  priorityCapacityRows: PriorityCapacityRow[];
  teamParticipation: TeamParticipation[];
  /** Package / Competing groups from Prioritization. Optional — absent means no group rules. */
  relationshipGroups?: ItemRelationshipGroupLike[];
  relationshipGroupMemberships?: ItemRelationshipGroupMembershipLike[];
  /** Workspace Item Effort band hours (Work Size / altitude → CU). */
  bandHours?: WorkSizeHoursOverrides | null;
  /**
   * Item id → the rank the organization accepted for it, 1-based. Applied to the
   * base priority so the plan is ordered by the ranking that was agreed; a rank
   * set deliberately in planning still overrides it.
   */
  acceptedRanks?: ReadonlyMap<string, number>;
}): PlanningEngineInput {
  const {
    items,
    edges,
    scenario,
    projectId,
    scopedItemIds,
    scopeKind,
    acceptedRanks,
    dbWorkUnits,
    dbDependencies,
    dbPriorityDependencies,
    dbPriorities = [],
    priorityOverrides,
    capacityRows,
    priorityCapacityRows,
    teamParticipation,
    bandHours,
  } = args;

  const filteredItems =
    scopedItemIds == null ? items : items.filter((item) => scopedItemIds.has(item.id));
  const idSet = new Set(filteredItems.map((i) => i.id));
  const filteredEdges = edges.filter(
    (e) => idSet.has(e.sourceItemId) && idSet.has(e.targetItemId),
  );

  const prioritiesFromItems = filteredItems.map((item) =>
    mergePriorityWithOverride(
      itemToPriority(item, scenario.id, bandHours, acceptedRanks?.get(item.id) ?? null),
      priorityOverrides.get(item.id),
    ),
  );
  const priorityIds = new Set(prioritiesFromItems.map((p) => p.id));

  // Unbound engine priorities (seeded or created on Priorities tab).
  // All Live Items: merge scenario-owned + unbound rows.
  // scenario_portfolio scope: the scope set IS this scenario's priority-row ids —
  // merge exactly those rows (they need not map to live items; "11 in scope"
  // must mean 11 rows in the run).
  // Other scoped inputs (pool/collection/…): stay exactly that item membership,
  // or Keith-style subsets inflate back to ~300.
  const unboundPriorities =
    scopedItemIds == null
      ? dbPriorities.filter(
          (p) =>
            !priorityIds.has(p.id) &&
            (p.scenarioId === scenario.id || p.scenarioId == null || p.scenarioId === ''),
        )
      : scopeKind === 'scenario_portfolio'
        ? dbPriorities.filter(
            (p) =>
              !priorityIds.has(p.id) &&
              p.scenarioId === scenario.id &&
              scopedItemIds.has(p.id),
          )
        : [];
  for (const p of unboundPriorities) priorityIds.add(p.id);
  const priorities = [...prioritiesFromItems, ...unboundPriorities];

  const workUnits = resolveScopedWorkUnitsForRun(
    filteredItems,
    dbWorkUnits,
    projectId,
    scopedItemIds == null ? null : idSet,
    bandHours,
  );
  const workUnitIds = new Set(workUnits.map((w) => w.id));

  const dependencies =
    dbDependencies.length > 0
      ? filterDependenciesToWorkUnits(dbDependencies, workUnitIds)
      : filteredEdges
          .filter((e) => e.status !== 'dissolved')
          .flatMap((e) => {
            const dep = edgeToWorkUnitDependency(e, projectId);
            return dep ? [dep] : [];
          });

  const priorityDependencies =
    dbPriorityDependencies.length > 0
      ? filterPriorityDepsToScope(dbPriorityDependencies, priorityIds)
      : filteredEdges
          .filter((e) => e.status !== 'dissolved')
          .flatMap((e) => {
            const dep = edgeToPriorityDependency(e, projectId);
            return dep ? [dep] : [];
          });

  const scopedParticipation = teamParticipation.filter(
    (tp) =>
      (tp.priorityId != null && priorityIds.has(tp.priorityId)) ||
      (tp.workUnitId != null && workUnitIds.has(tp.workUnitId)),
  );

  return {
    scenario,
    priorities,
    workUnits,
    dependencies,
    priorityDependencies,
    capacityRows,
    priorityCapacityRows,
    teamParticipation: scopedParticipation,
    relationshipGroups: [
      ...buildPlanningRelationshipGroups(
        args.relationshipGroups ?? [],
        args.relationshipGroupMemberships ?? [],
        priorities,
      ),
      // Elaboration spectrums are authored as edges rather than groups, but
      // constrain a plan the same way Competing does.
      ...buildElaborationSpectrumGroups(edges, priorities),
    ],
  };
}

/** Minimal shapes so the adapter does not depend on the Prioritization store's types. */
export interface ItemRelationshipGroupLike {
  id: string;
  type: string;
  name?: string | null;
  status?: string | null;
}
export interface ItemRelationshipGroupMembershipLike {
  groupId: string;
  itemId: string;
}

/**
 * Flattens Prioritization's Package / Competing groups into the shape the
 * engine understands.
 *
 * Only `package` and `exclusive` carry a planning consequence. Cluster is
 * identity (a de-duplication decision, resolved upstream), Collection is
 * curation, and Contains is reporting hierarchy that explicitly asserts no
 * prioritization weight — none of them constrain what can be delivered, so none
 * are translated here.
 *
 * Dissolved groups are dropped. Membership is kept even when a member is
 * outside the run's priorities: for a Package, a member that is not in this
 * plan is exactly the condition that makes the package undeliverable, so
 * discarding it would hide the violation. Groups that end up with fewer than
 * two members overall are dropped as meaningless.
 */
/**
 * Elaboration spectrums, as at-most-one groups.
 *
 * An Elaboration maps a parent need to a continuum of possibilities — minimally
 * sufficient through ideal — and you deliver **one** of them. Committing to two
 * options from the same spectrum means committing to two versions of the same
 * outcome, so the plan double-counts a need it can only meet once. Measured on
 * the Backspace workspace: 26 active elaboration edges, every one with demand
 * on both ends.
 *
 * Owner ruling (2026-07-21): treat it exactly as Competing — one above the line
 * is fine, two is a violation the user resolves. The engine never picks.
 *
 * Note this is deliberately *narrower* than making Elaboration a peer-level
 * exclusivity policy, which the relationship taxonomy explicitly says it is
 * not. Nothing here forbids authoring a spectrum, or reorders anything; the
 * constraint applies only to what a single plan may commit to at once.
 *
 * Spectrums are keyed by parent need, so two options under different parents
 * never conflict with each other. The parent itself is not a member — including
 * a need alongside the option that satisfies it is a whole-and-parts
 * double-count, which is a separate concern.
 */
export function buildElaborationSpectrumGroups(
  edges: ItemRelationshipEdge[],
  priorities: Priority[],
): PlanningRelationshipGroup[] {
  const optionsByParent = new Map<string, Set<string>>();
  for (const edge of edges) {
    if (edge.type !== 'elaboration') continue;
    if (edge.status === 'dissolved') continue;
    const options = optionsByParent.get(edge.sourceItemId);
    if (options) options.add(edge.targetItemId);
    else optionsByParent.set(edge.sourceItemId, new Set([edge.targetItemId]));
  }

  const priorityIds = new Set(priorities.map((p) => p.id));
  const out: PlanningRelationshipGroup[] = [];
  for (const [parentId, options] of optionsByParent) {
    // One option cannot conflict with itself, and a spectrum with at most one
    // option in this run can never be over-committed by it.
    const inRun = [...options].filter((id) => priorityIds.has(id));
    if (inRun.length < 2) continue;

    const parentName = priorities.find((p) => p.id === parentId)?.name ?? null;
    out.push({
      id: `elaboration:${parentId}`,
      rule: 'exclusive',
      label: parentName ? `possibilities for ${parentName}` : 'this possibility spectrum',
      memberPriorityIds: [...options],
    });
  }
  return out;
}

export function buildPlanningRelationshipGroups(
  groups: ItemRelationshipGroupLike[],
  memberships: ItemRelationshipGroupMembershipLike[],
  priorities: Priority[],
): PlanningRelationshipGroup[] {
  if (groups.length === 0) return [];

  const priorityIds = new Set(priorities.map((p) => p.id));
  const membersByGroup = new Map<string, string[]>();
  for (const m of memberships) {
    const list = membersByGroup.get(m.groupId);
    if (list) list.push(m.itemId);
    else membersByGroup.set(m.groupId, [m.itemId]);
  }

  const out: PlanningRelationshipGroup[] = [];
  for (const group of groups) {
    if (group.status === 'dissolved') continue;

    // A confirmed Cluster is a duplicate finding: the members describe one
    // underlying need, so a plan that commits to two of them is counting the
    // same work twice. That is the same shape as Competing — at most one may be
    // planned — so it reuses the exclusive rule rather than a third mechanism.
    //
    // Only `active` clusters count. A `candidate` cluster is an unconfirmed
    // suspicion of duplication, and blocking a plan on a proposal nobody has
    // accepted would be the engine asserting a dedup decision that has not been
    // made.
    const rule =
      group.type === 'package'
        ? 'package'
        : group.type === 'exclusive'
          ? 'exclusive'
          : group.type === 'cluster' && group.status === 'active'
            ? 'duplicate'
            : null;
    if (!rule) continue;

    const members = [...new Set(membersByGroup.get(group.id) ?? [])];
    if (members.length < 2) continue;

    // A group with no member in this run cannot be violated by it.
    if (!members.some((id) => priorityIds.has(id))) continue;

    out.push({ id: group.id, rule, label: group.name ?? null, memberPriorityIds: members });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Helpers for the live dashboard (no engine run needed)
// ---------------------------------------------------------------------------

export type WorkSizeBand = '1-2_weeks' | '3-8_weeks' | '9-26_weeks' | '27-52_weeks' | '52+_weeks' | 'unknown';

export interface LiveItemSummary {
  item: Item;
  workSizeBand: WorkSizeBand;
  estimatedHours: number;
  estimatedWUs: number;
}

export function buildLiveItemSummaries(
  items: Item[],
  bandHours?: WorkSizeHoursOverrides | null,
): LiveItemSummary[] {
  return items.map((item) => {
    const resolved = resolveItemEffortCapacityUnits(item, bandHours);
    const band = (resolved.workSizeBand ?? 'unknown') as WorkSizeBand;
    return {
      item,
      workSizeBand: band,
      estimatedHours: resolved.hours,
      estimatedWUs: resolved.capacityUnits ?? 0,
    };
  });
}

export const WORK_SIZE_BAND_LABELS: Record<WorkSizeBand, string> = {
  '1-2_weeks': '1–2 wk',
  '3-8_weeks': '3–8 wk',
  '9-26_weeks': '9–26 wk',
  '27-52_weeks': '27–52 wk',
  '52+_weeks': '52+ wk',
  'unknown': 'No size',
};
